import { useMemo } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DataTransfer } from '@cast/shared-types';
import { aggregatorByTimestampFn, sumDataTransferTraffic } from '@cast/utils';

import { useWorkloadNetworkDataTransfer } from './useWorkloadNetworkDataTransfer';

import {
  NetworkCostChartPoint,
  NetworkCostChartTotals,
} from './useNetworkCostChartData';

dayjs.extend(utc);

export type NetworkTrafficChartPoint = NetworkCostChartPoint;
export type NetworkTrafficChartTotals = NetworkCostChartTotals;

export const useNetworkTrafficChartData = () => {
  const { data, isLoading, isFetching, error, refetch, timeZone } =
    useWorkloadNetworkDataTransfer();

  const dataPoints: NetworkTrafficChartPoint[] = useMemo(() => {
    const item = data?.item;

    if (!item) {
      return [];
    }

    const byTimestamp: Record<string, DataTransfer> = {};

    item.egressMetrics?.forEach(
      aggregatorByTimestampFn.bind(null, byTimestamp, 'egress')
    );
    item.ingressMetrics?.forEach(
      aggregatorByTimestampFn.bind(null, byTimestamp, 'ingress')
    );

    return Object.keys(byTimestamp)
      .sort()
      .map((timestamp) => ({
        timestamp,
        chartTimestampUnix: dayjs.utc(timestamp).valueOf(),
        egress: sumDataTransferTraffic(byTimestamp[timestamp].egress),
        ingress: sumDataTransferTraffic(byTimestamp[timestamp].ingress),
      }));
  }, [data]);

  const totals = useMemo(() => {
    return dataPoints.reduce(
      (acc, point) => ({
        egress: acc.egress + point.egress,
        ingress: acc.ingress + point.ingress,
        total: acc.total + point.egress + point.ingress,
      }),
      { egress: 0, ingress: 0, total: 0 }
    );
  }, [dataPoints]);

  return {
    data: dataPoints,
    totals,
    isLoading,
    isFetching,
    error,
    refetch,
    timeZone,
  };
};
